import React, { useState, useEffect, useRef } from 'react';  
import { useParams } from 'react-router-dom';  
import TitledField from '../../../../../Components/Utils/TitledField/TitledField';  
import CreationBar from '../../../../../Components/Utils/Actions/CreationBar/CreationBar';  
import ActionBar from '../../../../../Components/Utils/Actions/ActionBar/ActionBar';  
import layout from '../../../../../Modules/layout.module.css';  
import styles from './QuestionsView.module.css';
import { GetQuestionsByTemplateIdAndCriteriaId, CreateQuestionInQuestionnaireTemplate, DeleteQuestionsInCriteriaTemplate, ReorderQuestionInCriteriaTemplate } from '../../../../../API/TemplateBank/TemplateBank';
import { UpdateQuestionnaireQuestion } from '../../../../../API/Evaluate/Evaluate';
import { IQuestionnaireQuestion } from '../../../../../Types/Products/IQuestionnaireQuestion';
import { useAppSelector } from '../../../../../Redux/Store/Store';
import { getAiPowered, getClient } from '../../../../../Redux/Store/Slices/client';
import Button from '../../../../../Components/Utils/Button/Button';
import { axiosInstance } from '../../../../../Authentication/AxiosInterceptor';
import Popup from '../../../../../Components/Utils/Popup/Popup';
import FloatingLabelTextArea from '../../../../../Components/Utils/Form/FloatingLabelTextArea/FloatingLabelTextArea';
import LoadingIcon from '../../../../../Components/Utils/LoadingIcon/LoadingIcon';
import Checkbox from '../../../../../Components/Utils/Checkbox/Checkbox';
import { motion, AnimatePresence } from 'framer-motion';
import Tooltip from '../../../../../Components/Utils/Tooltip/Tooltip';
import { beginPendingSave } from '../../../../../Hooks/useUnsavedChangesWarning';
import { toast } from 'react-toastify';

type DefineQuestionsProps = {
  fullCriteria: string | undefined;
  criteriaChangedHandler: (text: string) => void;
};


const DEBOUNCE_DELAY = 500;

const QuestionsView = ({ fullCriteria, criteriaChangedHandler }: DefineQuestionsProps) => {  
  const { templateId, templateTitle, sectionId, criteriaId } = useParams();  
  
  const [selectedItems, setSelectedItems] = useState<string[]>([]);  
  const [questions, setQuestions] = useState<IQuestionnaireQuestion[]>([]);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [showCriteriaPopup, setShowCriteriaPopup] = useState(false);

  const client = useAppSelector(getClient);
  const aiPowered = useAppSelector(getAiPowered);
  const updateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const endPendingSaveRef = useRef<(() => void) | null>(null);
  
  useEffect(() => {
    if (!templateId || !criteriaId || !client) return;

    const loadQuestions = async () => {
      const response = await GetQuestionsByTemplateIdAndCriteriaId(templateId, criteriaId, client.id);
      setQuestions(response);
    };

    loadQuestions();
  }, [templateId, criteriaId, client]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
      endPendingSaveRef.current?.();
      endPendingSaveRef.current = null;
    };
  }, []);
  
  const itemSelected = (itemId: string, selected: boolean) => {  
    setSelectedItems((prevState) => {  
      if (selected) {  
        // Add itemId to the list  
        return [...prevState, itemId];  
      } else {  
        // Remove itemId from the list  
        return prevState.filter((id) => id !== itemId);  
      }  
    });  
  };  
  
  const handleQuestionTextChange = async (itemId: string, newText: string) => {
    const question = questions.find((q) => q.id === itemId);

    setQuestions((prev) =>
      prev.map((q) =>
        q.id === itemId ? { ...q, question: newText } : q
      )
    );

    if (!question || !client) return;

    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
    } else if (!endPendingSaveRef.current) {
      endPendingSaveRef.current = beginPendingSave();
    }

    updateTimerRef.current = setTimeout(async () => {
      try {
        const updatedQuestion = { ...question, question: newText };
        await UpdateQuestionnaireQuestion(updatedQuestion);
      } catch (error) {
        console.error('Error updating question:', error);
      } finally {
        updateTimerRef.current = null;
        endPendingSaveRef.current?.();
        endPendingSaveRef.current = null;
      }
    }, DEBOUNCE_DELAY);
  };

  const handleLockToggle = async (itemId: string) => {
    const question = questions.find((q) => q.id === itemId);
    if (!question || !client) return;

    const updatedQuestion = { ...question, locked: !question.locked };
    
    try {
      await UpdateQuestionnaireQuestion(updatedQuestion);
      setQuestions((prev) =>
        prev.map((q) =>
          q.id === itemId ? { ...q, locked: !q.locked } : q
        )
      );
    } catch (error) {
      console.error('Error toggling question lock:', error);
    }
  };  
  
  const createItem = async (questionText: string) => {  
    if(templateId && sectionId && criteriaId && client) {
      const newQuestion = await CreateQuestionInQuestionnaireTemplate(templateId, sectionId, criteriaId, questionText, client.id);
      setQuestions([...questions, newQuestion]);  
    }
  };  

  const deleteItems = async () => {  

    if(templateId && sectionId && criteriaId && client){
        await DeleteQuestionsInCriteriaTemplate(templateId, sectionId, criteriaId, selectedItems, client.id);

        setQuestions((prev) =>  
            prev.filter((item) => !selectedItems.includes(item.id)) 
        );  

        setSelectedItems([]); // Clear the selected items after deletion  
    }
  };  

  const createFromCriteria = async () => {
    if (!fullCriteria) return;
    
    setLoadingCreate(true);
    try {
      const response = await axiosInstance.post(
        `/GenerateQuestionsByGuidance/${templateId}/${client?.id}/${sectionId}/${criteriaId}`, 
        fullCriteria
      );
      const generatedQuestions = response.data as IQuestionnaireQuestion[];
      setQuestions((prev) => [...prev, ...generatedQuestions]);
      setShowCriteriaPopup(false);
    } catch (error) {
      console.error('Error generating questions from criteria:', error);
      toast.error('Could not generate questions from this criteria. Please try again.');
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleSelectAll = (selected: boolean) => {
    setSelectedItems(selected ? questions.map(q => q.id) : []);
  };

  const handleMoveQuestion = async (questionId: string, direction: 'up' | 'down') => {
    if (!templateId || !sectionId || !criteriaId || !client) return;

    try {
      const updatedQuestions = await ReorderQuestionInCriteriaTemplate(
        templateId, 
        sectionId, 
        criteriaId, 
        questionId, 
        direction, 
        client.id
      );
      
      setQuestions(updatedQuestions);
    } catch (error) {
      console.error('Error reordering question:', error);
    }
  };

  return (  
    <div className={layout.container}>  
      <div className={layout.column1}>   
        <div className={styles.actionBarSection}>
          <ActionBar enabledFeatures={['delete']} selectedCount={selectedItems.length} onDeleteHandler={deleteItems}/>  
        </div>

        <div className={styles.questionsSection}>
          <div className={styles.questionsHeader}>
            <div>
              <p className={styles.sectionTitle}>Questions</p>
              <p className={styles.sectionHint}>Create the questionnaire used for this criteria. These will guide participants through the diagnostic process ensuring they are able to answer the criteria accurately.</p>
            </div>
            <div className={styles.questionsHeaderActions}>
              <span className={styles.questionCountBadge}>{questions.length} questions</span>
              <button className={styles.selectAllContainer} onClick={() => handleSelectAll(selectedItems.length !== questions.length)}>
                <Checkbox clickHandler={handleSelectAll} value={selectedItems.length === questions.length && questions.length > 0} />
                <p>Select All</p>
              </button>
            </div>
          </div>
       

       <AnimatePresence mode="popLayout">
        {questions.map(({ id, question, locked, isFromMesmaTemplate }, index) => {
            return (
                <motion.div 
                    key={id} 
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ 
                        duration: 0.2,
                        layout: { duration: 0.3, ease: "easeInOut" }
                    }}
                    className={styles.questionContainer}
                >
                    <div className={styles.questionField}>
                        <TitledField  
                        title={`Q${index + 1}`}
                        text={question}  
                        locked={locked}
                        onLockToggle={() => handleLockToggle(id)}
                        onSelectedHandler={(selected: boolean) => itemSelected(id, selected)}  
                        titleChanged={(newText: string) => handleQuestionTextChange(id, newText)}  
                        valueChanged={(newText: string) => handleQuestionTextChange(id, newText)}  
                        selected={selectedItems.includes(id)}
                        />
                    </div>
                    <div className={styles.reorderButtons}>
                        <button 
                            className={styles.reorderButton}
                            onClick={() => handleMoveQuestion(id, 'up')}
                            disabled={index === 0}
                            title="Move up"
                        >
                            ▲
                        </button>
                        <button 
                            className={styles.reorderButton}
                            onClick={() => handleMoveQuestion(id, 'down')}
                            disabled={index === questions.length - 1}
                            title="Move down"
                        >
                            ▼
                        </button>
                    </div>
                </motion.div>  
            );  
        })}
        </AnimatePresence>
        </div>

        <div className={styles.creationSection}>
          <div className={styles.creationSectionHeader}>
            <p className={styles.sectionTitle}>Add Questions</p>
            <p className={styles.sectionHint}>Create individual questions manually or generate them from the full criteria.</p>
          </div>
        <div className={styles.creationBarContainer}>
        
        <Tooltip text="AI powered feature. Please upgrade to access." align="top" disabled={aiPowered}>
          <Button disabled={!aiPowered} className={styles.generateButton} clickHandler={() => setShowCriteriaPopup(true)}>Create from criteria <img src='/img/sparkle.svg'/></Button>
        </Tooltip>

        <CreationBar  
                onCreateHandler={createItem}  
                placeholder={'Enter new question...'}  
                noTextMessage={'*'}  
                />  

        </div>
        </div>

       

        <Popup isVisible={showCriteriaPopup} onClose={() => setShowCriteriaPopup(false)}>
          <div className={styles.popupContent}>
            {loadingCreate ? 
            <LoadingIcon/> :
            <>
              <FloatingLabelTextArea style={{ width: "100%" }} height={"100%"} onChange={(value) => criteriaChangedHandler(value) } value={fullCriteria} label={'Enter criteria for Mia to use when generating questions'} />
              <Button className={styles.generateButton} clickHandler={() => createFromCriteria() }>Generate questions <img src='/img/sparkle.svg'/></Button>
            </>
            }
          </div>
        </Popup>
      </div>  
    </div>  
  );  
};  
  
export default QuestionsView;  